// PRIMEIRO import, de proposito (spec `tutorial-crm` 6.1): no iframe do
// tutorial (`?tutorial=1`) ele troca localStorage/sessionStorage por memoria e
// corta a rede ANTES de qualquer store persistido, `services/api.ts` ou o
// i18next serem avaliados — modulos ES avaliam na ordem dos imports. Fora do
// tutorial, nao faz nada.
import "./tutorial/isolation";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { SessionGate } from "./components/SessionGate";
import { TenantProvider } from "./providers/TenantProvider.tsx";
import { reloadOnChunkError } from "./lib/lazyWithRetry";
import { reportErrorDeferred } from "./lib/report-error-deferred";
import { isTutorialMode } from "./tutorial/mode";
import { postTutorialMessage } from "./tutorial/messages";
import { initTheme } from "./stores/theme";
import "./index.css";

// Initialize i18n
import '@/locales';

const tutorial = isTutorialMode();

// Tema (claro/escuro/sistema) aplicado ao <html> ANTES do primeiro render — sem
// isso a primeira pintura sai clara e "pisca" para o escuro. Tambem passa a
// ouvir a troca de tema do aparelho quando a escolha e' "seguir o sistema".
initTheme();

// Core Web Vitals (LCP/CLS/INP|FID/TTFB) — dependency-free, no-ops where the
// PerformanceObserver API or specific entry types are unsupported. Desligado no
// iframe do tutorial (spec 6.1-6).
//
// Import dinamico DEPOIS do `load` (spec `crm-alvo-v2-fechamento`, fatia 6):
// o coletor e o reporter saem do chunk inicial. Nao se perde medida — os
// observers usam `buffered: true` e recebem as entradas anteriores ao registro;
// o TTFB vem do Navigation Timing, que fica disponivel a qualquer momento.
function startWebVitals(): void {
  import("./lib/web-vitals")
    .then(({ initWebVitals }) => initWebVitals())
    .catch((error: unknown) => {
      // oprus-allow:swallow — telemetria best-effort; sem ela o app segue igual.
      console.warn("[web-vitals] coletor indisponivel", error);
    });
}

if (!tutorial) {
  if (document.readyState === "complete") {
    startWebVitals();
  } else {
    window.addEventListener("load", startWebVitals, { once: true });
  }
}

// Safety net: reload when a stale dynamic-import chunk surfaces outside of
// React's error boundary (event handlers, prefetches, etc).
window.addEventListener('error', (event) => {
  if (reloadOnChunkError(event.error ?? event.message)) {
    event.preventDefault();
    return;
  }
  void reportErrorDeferred(event.error ?? event.message, { kind: 'window-error' });
});
window.addEventListener('unhandledrejection', (event) => {
  if (reloadOnChunkError(event.reason)) {
    event.preventDefault();
    return;
  }
  void reportErrorDeferred(event.reason, { kind: 'unhandled-rejection' });
});

function render(): void {
  createRoot(document.getElementById("root")!).render(
    <SessionGate><TenantProvider>
      <App />
    </TenantProvider></SessionGate>
  );
}

if (tutorial) {
  // A sessao de demonstracao (perfil, respostas de exemplo, textos da tela) tem
  // de estar pronta antes do primeiro render. Falhou: o app nao e' montado e a
  // janela real mostra o erro na propria sobreposicao.
  import("./tutorial/bootstrap")
    .then(({ bootstrapTutorial }) => bootstrapTutorial())
    .then(render)
    .catch((error: unknown) => {
      console.error("[tutorial] falha ao preparar a tela de exemplo", error);
      if (window.parent !== window) {
        postTutorialMessage(window.parent, {
          type: "tutorial:error",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    });
} else {
  render();
}
